// hooks/useLivePolling.js
import { useEffect, useRef } from "react";

/**
 * Polls `callback` on an interval, but:
 *  - pauses while the tab is hidden (document.visibilityState !== "visible")
 *  - re-fires once immediately when the tab becomes visible again,
 *    so data isn't stale after a long background period
 *  - jitters the interval (+/- ~15%) so many components polling at the
 *    same base interval don't all hit the backend on the exact same tick
 *
 * @param {Function} callback   - function to call on each poll (sync or async)
 * @param {number}   intervalMs - base interval in ms (e.g. 30000)
 * @param {Array}    deps       - dependency array; polling restarts when these change
 * @param {boolean}  enabled    - set false to disable polling entirely
 *                                 (e.g. no bins selected, chart not visible on screen)
 */
export function useLivePolling(callback, intervalMs, deps = [], enabled = true) {
    // Keep the latest callback in a ref so we don't have to restart the
    // interval every time the callback identity changes for unrelated reasons.
    const callbackRef = useRef(callback);
    callbackRef.current = callback;

    useEffect(() => {
        if (!enabled) return;

        let timeoutId;
        let stopped = false;

        const jitter = () => intervalMs * (1 + (Math.random() * 0.3 - 0.15));

        const tick = () => {
            if (stopped) return;
            if (document.visibilityState === "visible") {
                callbackRef.current();
            }
            timeoutId = setTimeout(tick, jitter());
        };

        // Tab regains focus after being backgrounded -> refresh right away
        // instead of waiting out the rest of the paused interval.
        const handleVisibility = () => {
            if (document.visibilityState === "visible") {
                callbackRef.current();
            }
        };
        document.addEventListener("visibilitychange", handleVisibility);

        timeoutId = setTimeout(tick, jitter());

        return () => {
            stopped = true;
            clearTimeout(timeoutId);
            document.removeEventListener("visibilitychange", handleVisibility);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, intervalMs, ...deps]);
}